import type { EnchantRow, RecipeRow, RecipesResponse } from '@brimstone/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { EmptyState, KpiTile, PageLayout, Panel, Tag } from '../../components/ui';
import { useRecipes } from '../../lib/tracking';
import { ShowOnMap } from '../../components/ShowOnMap';

const SOURCE_TYPES: Record<string, string> = {
  drop: 'Drop',
  'world drop': 'World drop',
  vendor: 'Vendor',
  trainer: 'Trainer',
  quest: 'Quest',
  reputation: 'Reputation',
};

function Known({ known }: { known: boolean | null }) {
  if (known === null) return <span className="font-mono text-label text-muted">NO DATA</span>;
  return known ? (
    <span className="font-mono text-label text-accent">KNOWN ✓</span>
  ) : (
    <span className="font-mono text-label text-muted">MISSING</span>
  );
}

function Source({ r }: { r: RecipeRow }) {
  const s = r.source;
  if (!s || s.sourceType === 'unknown') {
    return (
      <>
        <span className="text-muted">Source unknown</span>
        {s?.detail && <span className="block text-secondary text-muted">{s.detail}</span>}
      </>
    );
  }
  return (
    <>
      <span>
        {SOURCE_TYPES[s.sourceType] ?? s.sourceType}
        {s.location ? ` · ${s.location}` : ''} <ShowOnMap link={s.map} />
      </span>
      {s.detail && <span className="block text-secondary text-muted">{s.detail}</span>}
    </>
  );
}

const name = (id: string | null, label: string): ReactNode =>
  id ? <Link to={`/book/${id}`}>{label}</Link> : label;

function RecipeItem({ r }: { r: RecipeRow }) {
  return (
    <li className="flex flex-col gap-1.5 border-b border-line-soft px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="text-name">{r.item}</span>
          {r.boundToCrafter && <Tag>BOUND</Tag>}
        </span>
        <Known known={r.known} />
      </div>
      <div className="text-secondary text-muted">
        {r.profession}
        {r.specialization ? ` (${r.specialization})` : ''} · for {r.forNames.join(', ')}
        {r.spec ? ` · ${r.spec}` : ''}
        {r.note ? ` · ${r.note}` : ''}
      </div>
      {r.known !== true && (
        <div className="text-compact">
          <Source r={r} />
        </div>
      )}
    </li>
  );
}

function ByCrafter({ data }: { data: RecipesResponse }) {
  const crafters = [...new Set(data.recipes.map((r) => r.crafterName))];
  return (
    <>
      {crafters.map((crafter) => {
        const rows = data.recipes.filter((r) => r.crafterName === crafter);
        const known = rows.filter((r) => r.known).length;
        return (
          <Panel
            key={crafter}
            title={name(rows[0]!.crafterId, crafter)}
            meta={`${known}/${rows.length} KNOWN`}
          >
            <ul className="m-0 list-none p-0">
              {rows.map((r) => (
                <RecipeItem key={r.id} r={r} />
              ))}
            </ul>
          </Panel>
        );
      })}
    </>
  );
}

function Enchants({ enchants }: { enchants: EnchantRow[] }) {
  return (
    <Panel title="Wanted enchants" level="side" meta={String(enchants.length)}>
      {enchants.length === 0 ? (
        <EmptyState>No enchants on the wanted list.</EmptyState>
      ) : (
        <ul className="m-0 list-none p-0">
          {enchants.map((e) => (
            <li
              key={e.id}
              className="flex flex-col gap-1 border-b border-line-soft px-4 py-3 text-compact last:border-b-0"
            >
              <span className="flex flex-wrap items-baseline justify-between gap-2">
                <span>{e.enchant}</span>
                <Known known={e.known} />
              </span>
              <span className="text-secondary text-muted">
                {name(e.enchanterId, e.enchanterName)} · for {e.forNames.join(', ')}
                {e.nameUnverified && ' · name not confirmed on Forever'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** /recipes (SPEC §6.12): the wanted list by crafter, known or where to find it. */
export function RecipesPage() {
  const q = useRecipes();
  const data = q.data;
  const known = data?.recipes.filter((r) => r.known).length;
  return (
    <PageLayout
      kpis={
        <>
          <KpiTile label="Wanted" value={data ? data.recipes.length : '—'} />
          <KpiTile label="Known" value={known ?? '—'} />
          <KpiTile
            label="No source yet"
            value={
              data
                ? data.recipes.filter((r) => !r.source || r.source.sourceType === 'unknown').length
                : '—'
            }
          />
        </>
      }
      main={
        !data ? (
          <Panel title="Recipes">
            <EmptyState>{q.isError ? 'Couldn’t load the recipes.' : 'Loading…'}</EmptyState>
          </Panel>
        ) : data.recipes.length === 0 ? (
          <Panel title="Recipes">
            <EmptyState>No wanted recipes in the roster doc.</EmptyState>
          </Panel>
        ) : (
          <>
            <p className="m-0 text-secondary text-muted">
              Sources are compiled from addon data and unverified until confirmed in game.
            </p>
            <ByCrafter data={data} />
          </>
        )
      }
      side={data && <Enchants enchants={data.enchants} />}
    />
  );
}
